import type { RouteLocation, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import {
  checkoutRouteName,
  getCollectorRoute,
  getCollectorRoutes,
  offerCreateRouteName,
  offerEditRouteName,
} from '@/views/collector-routes';
import { sharedRoutes } from '@/shared-routes';
import { i18n } from '@/i18n';
import { useWhiteLabelStore } from '@/stores/white-label/WhiteLabelStore';
import { checkActivationPublished, checkActivationUnPublished, setWhiteLabelForLIB } from '@/utils/router-utils';
import { collectorRoutePaths } from '@/views/collector-route-paths';
import { isActivationLIB } from '@/utils/router-utils';

const { t } = i18n.global;

export const determineRoutes = (): Array<RouteRecordRaw> => {
  return [
    {
      path: '/',
      component: () => import('@/views/linkInBio/LinkInBio.vue'),
      beforeEnter: [async (to, from, next) => {
        if (await isActivationLIB()) {
          await setWhiteLabelForLIB();
          next();
        } else {
          next(getCollectorRoute(collectorRoutePaths.LISTINGS));
        }
      }],
    },
    ...getCollectorRoutes(),
    {
      path: sharedRoutes.LOGOUT,
      component: () => import('./views/logout/Logout.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: sharedRoutes.LOGIN,
      component: () => import('./views/login/Login.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: sharedRoutes.RESET_PASSWORD,
      component: () => import('./views/reset-password/ResetPassword.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: sharedRoutes.UNVERIFIED,
      component: () => import('./views/unverified/Unverified.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: sharedRoutes.NOT_FOUND,
      component: () => import('./views/not-found/NotFound.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: sharedRoutes.SITE_NOT_FOUND,
      meta: { noSite: true },
      component: () => import('./views/not-found/NotFound.vue'),
      beforeEnter: [checkActivationPublished],
    },
    {
      meta: { title: t('storefront.app.error.404.title') },
      path: '/:catchAll(.*)*',
      component: () => import('@/views/not-found/NotFound.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
  ];
};

const routes = determineRoutes();

const shouldPreventScroll = (to: RouteLocation, from: RouteLocation) => {
  const isSamePath = to.path === from.path;
  // check query is the same except for page parameter
  const isSameQuery = JSON.stringify({ ...to.query, page: 1 }) === JSON.stringify({ ...from.query, page: 1 });
  const isOnDemandPath =
    to.path === collectorRoutePaths.LISTINGS ||
    (to.params?.id !== undefined &&
      to.path === getCollectorRoute(collectorRoutePaths.SELLER_DETAIL, `${to.params.id}`));

  return isSamePath && isSameQuery && isOnDemandPath;
};

const shouldDelayScroll = (to: RouteLocation, from: RouteLocation) => {
  const isSamePath = to.path === from.path;
  // check query is the same except for page parameter
  const isOnDemandPath =
    to.path === collectorRoutePaths.LISTINGS ||
    (to.params?.id !== undefined &&
      to.path === getCollectorRoute(collectorRoutePaths.SELLER_DETAIL, `${to.params.id}`));

  return !isSamePath && isOnDemandPath;
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollBehavior: (to: RouteLocation, from: RouteLocation, savedPosition: any | null) => {
    const transitionTime = 225; // neon-fade-transition time

    if (!shouldPreventScroll(to, from)) {
      setTimeout(
        () => {
          window.scrollTo({
            left: savedPosition?.left ?? 0,
            top: savedPosition?.top ?? 0,
            behavior: 'instant',
          } as unknown as ScrollOptions); // required cast due to typscript missing behavior = instant property
        },
        shouldDelayScroll(to, from) ? 1200 : transitionTime,
      );
    }
  },
});

router.afterEach((to: RouteLocation) => {
  const {
    query: { step },
    name,
    meta: { title },
  } = to;

  setTimeout(async () => {
    const { activation } = useWhiteLabelStore();

    if (step && name === checkoutRouteName) {
      document.title = activation
        ? `${t(`storefront.app.checkout.page.title.${step}`)} - ${activation.name}`
        : t('storefront.app.checkout.page.title');
      return;
    }

    if (step && (name === offerCreateRouteName || name === offerEditRouteName)) {
      document.title = activation
        ? `${t(`storefront.app.offer.page.title.${step}`)} - ${activation.name}`
        : t('storefront.app.offers.menu.label');
      return;
    }

    document.title = title && activation ? `${title} - ${activation.name}` : t('storefront.app.meta.default.title');
  });
});

export default router;

router.onError((error, to, from) => {
  console.error(error, to, from);
});
